// packages/systems/component-canvas/designer/utils/injectGlobalComponentsForCanvas.ts

import {Component, Expressions, Plugins} from '@packages/systems/wfdl';
import {
  EElement,
  ERecord,
  EList,
  EText,
  EBoolean,
  Expression,
  EUnion,
} from '@packages/systems/wfdl-ast';
import {qualify} from '@packages/systems/wfdl-Qualified';
import {GuidGenerator} from '@packages/utilities/guid';
import {isBody} from '@packages/utilities/elementTypeUtils';
import {weakMemo} from '@packages/utilities/memo';

// ID added to the DOM node containing global component instances, when they exist
const GLOBAL_COMPONENT_CONTAINER_ID = 'wf-global-components-canvas-container';

/**
 * Injects global component instances into the page render expression for
 * display-only purposes in the component canvas.
 *
 * These instances are NOT persisted - they only exist at runtime to allow
 * HTML embeds (custom CSS/JS) within global components to affect the canvas.
 *
 * The global components are rendered in a visually hidden container that
 * doesn't interfere with the visual editing experience.
 */
function _injectGlobalComponentsForComponentCanvas(
  rootExpression: Expression,
  componentMap: Component.ComponentMap,
  globalComponentIds: string[]
): Expression {
  if (globalComponentIds.length === 0) {
    return rootExpression;
  }

  // We only want to inject global component instances into the Body element.
  // Although this function expects to be called with the page's root render expression,
  // this steps ensures we're only injecting global components into a Body element
  const rootEl = Expressions.getElement(rootExpression);
  if (!rootEl || !isBody(rootEl)) {
    return rootExpression;
  }

  // When there are global components configured, an instance of each is created inside of
  // a visually hidden container element which is prepended to the page's Body content.
  const containerEl = createGlobalComponentsContainer(
    globalComponentIds,
    componentMap
  );

  return Expressions.updateElement(
    Expressions.getElement(rootExpression)?.id ?? '',
    (bodyElement) => {
      const bodyData = Expressions.getElementData(bodyElement);
      const existingChildren = Expressions.getIn(bodyData, [
        {in: 'Record', at: 'children'},
      ]);
      const childrenList = existingChildren
        ? (Expressions.getList(existingChildren) ?? [])
        : [];

      // Prepend hidden container
      const updatedChildren = EList([containerEl, ...childrenList]);
      const updatedData = Expressions.sparseSetIn(
        bodyData,
        [{in: 'Record', at: 'children'}],
        updatedChildren
      );

      return Expressions.setElementData(bodyElement, updatedData);
    },
    rootExpression
  );
}

/**
 * Memoized version of `_injectGlobalComponentsForComponentCanvas`
 */
export const injectGlobalComponentsForComponentCanvas = weakMemo(
  (rootExpression: Expression) =>
    weakMemo((componentMap: Component.ComponentMap) =>
      weakMemo((globalComponentIds: string[]) =>
        _injectGlobalComponentsForComponentCanvas(
          rootExpression,
          componentMap,
          globalComponentIds
        )
      )
    )
);

/**
 * Builds the hidden `Basic::Block` container of global component instances
 * shared between the Component Canvas and the component preview iframe. The
 * container itself uses `visibility.conditions = [false]` + `keepInHtml = true`
 * so embedded CSS / JS inside globals still applies, but it takes no visual
 * space. Missing component ids are silently filtered out.
 *
 * Used by:
 * - {@link injectGlobalComponentsForComponentCanvas} (prepends to page Body)
 * - `injectGlobalComponentsForComponentPreview` (wraps the previewed render)
 */
export function createGlobalComponentsContainer(
  globalComponentIds: string[],
  componentMap: Component.ComponentMap
): Expression {
  const guidGenerator = new GuidGenerator();
  const containerId = guidGenerator.getNext();

  const componentInstances = globalComponentIds
    .filter((componentId) => {
      // Validate component exists
      const componentType = qualify(
        Plugins.name('__SitePlugin'),
        Component.name(componentId)
      );
      return componentMap.has(componentType);
    })
    .map((componentId) => {
      const instanceId = guidGenerator.getNext();
      return EElement({
        id: instanceId,
        type: qualify(
          Plugins.name('__SitePlugin'),
          Component.name(componentId)
        ),
        data: ERecord({}),
      });
    });

  // Create a visually hidden div to hold global component instance.
  return EElement({
    id: containerId,
    type: qualify(Plugins.name('Basic'), Component.name('Block')),
    data: ERecord({
      children: EList(componentInstances),
      visibility: ERecord({
        conditions: EList([EBoolean(false)]),
        keepInHtml: EUnion({tag: 'True', value: ERecord({})}),
      }),
      attr: ERecord({
        id: EText(GLOBAL_COMPONENT_CONTAINER_ID),
      }),
    }),
  });
}
